import React, { useEffect, useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";
import { Skeleton } from "../components/ui/skeleton";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
} from "../components/ui/chart";
import dayjs from "dayjs";
import Nav from "../components/Nav";
import { useAuth0 } from "@auth0/auth0-react";
import { useLocalStorage } from "./Composer";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const calculateDateRanges = () => {
  const now = dayjs();
  const currentYear = now.year();
  const lastYear = currentYear - 1;

  return {
    last_7_days: {
      start: now.subtract(7, "days").format("YYYY-MM-DD"),
      end: now.format("YYYY-MM-DD"),
    },
    month_to_date: {
      start: now.startOf("month").format("YYYY-MM-DD"),
      end: now.format("YYYY-MM-DD"),
    },
    last_30_days: {
      start: now.subtract(30, "days").format("YYYY-MM-DD"),
      end: now.format("YYYY-MM-DD"),
    },
    last_90_days: {
      start: now.subtract(90, "days").format("YYYY-MM-DD"),
      end: now.format("YYYY-MM-DD"),
    },
    last_180_days: {
      start: now.subtract(180, "days").format("YYYY-MM-DD"),
      end: now.format("YYYY-MM-DD"),
    },
    last_365_days: {
      start: now.subtract(365, "days").format("YYYY-MM-DD"),
      end: now.format("YYYY-MM-DD"),
    },
    // Current year quarters
    [`q1_${currentYear}`]: {
      start: `${currentYear}-01-01`,
      end: `${currentYear}-03-31`,
    },
    [`q2_${currentYear}`]: {
      start: `${currentYear}-04-01`,
      end: `${currentYear}-06-30`,
    },
    [`q3_${currentYear}`]: {
      start: `${currentYear}-07-01`,
      end: `${currentYear}-09-30`,
    },
    [`q4_${currentYear}`]: {
      start: `${currentYear}-10-01`,
      end: `${currentYear}-12-31`,
    },
    // Previous year quarters
    [`q1_${lastYear}`]: {
      start: `${lastYear}-01-01`,
      end: `${lastYear}-03-31`,
    },
    [`q2_${lastYear}`]: {
      start: `${lastYear}-04-01`,
      end: `${lastYear}-06-30`,
    },
    [`q3_${lastYear}`]: {
      start: `${lastYear}-07-01`,
      end: `${lastYear}-09-30`,
    },
    [`q4_${lastYear}`]: {
      start: `${lastYear}-10-01`,
      end: `${lastYear}-12-31`,
    },
  };
};

const Analytics = () => {
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const name = user ? user.name : "Stranger";

  const [compellingUser, setCompellingUser] = useLocalStorage(
    "local-compelling-user",
    { fullName: "Stranger" }
  );

  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  const [availableCampaigns, setAvailableCampaigns] = useState([]);
  const [campaignNames, setCampaignNames] = useState({});
  const [summaryData, setSummaryData] = useState(null);
  const [timedData, setTimedData] = useState(null);
  const [allData, setAllData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState("month_to_date");
  const [chartData, setChartData] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [activeTab, setActiveTab] = useState("activity");
  const [stepData, setStepData] = useState([]);
  const [stepLoading, setStepLoading] = useState(false);
  const [chartAggregation, setChartAggregation] = useState("daily");

  const dateRanges = calculateDateRanges();

  const fetchAllCampaignAnalytics = async () => {
    try {
      const response = await fetch(
        "https://api.compelling.ai/instantly/OppyAnalytics",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            time_period: "last_365_days",
          }),
        }
      );

      const result = await response.json();
      
      // Add defensive checks for the response structure
      if (!result || typeof result !== 'object') {
        console.error('Invalid response format');
        return [];
      }

      let campaignData = [];
      
      // Handle both possible response formats
      if (Array.isArray(result)) {
        campaignData = result;
      } else if (result.campaign_data_mongo?.data) {
        campaignData = result.campaign_data_mongo.data;
      }
      
      // Update campaign names from the fetched data, but preserve existing names
      setCampaignNames(prev => {
        const newNames = { ...prev };
        campaignData.forEach(campaign => {
          if (campaign?.campaign && campaign?.campaign_name) {
            newNames[campaign.campaign] = campaign.campaign_name;
          }
        });
        return newNames;
      });
      
      setAllData(campaignData);
      return campaignData;
    } catch (error) {
      console.error('Error fetching campaign analytics:', error);
      setError(error.toString());
      return [];
    }
  };

  const fetchSummaryData = async (campaignIdToFetch = selectedCampaignId) => {
    if (!campaignIdToFetch) {
      console.warn("fetchSummaryData called without a valid campaign ID.");
      return;
    }
    try {
      const response = await fetch(
        "https://api.compelling.ai/instantly/OppyAnalytics",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            campaign_id: campaignIdToFetch,
          }),
        }
      );

      const result = await response.json();
      // Update campaign name when we get summary data, preserving other names
      // Always update the campaignNames map so dropdown options get populated.
      if (result.campaign_name && campaignIdToFetch) {
        setCampaignNames(prev => ({
          ...prev,
          [campaignIdToFetch]: result.campaign_name
        }));
      }

      // Return the fetched result
      return result;
    } catch (error) {
      setError(error.toString());
      console.error(`Error fetching summary data for ${campaignIdToFetch}:`, error);
      return null;
    }
  };

  const fetchTimedData = async (start, end, campaignIdToFetch = selectedCampaignId) => {
    if (!campaignIdToFetch) {
      console.warn("fetchTimedData called without a valid campaign ID.");
      return;
    }

    try {
      const response = await fetch(
        "https://api.compelling.ai/instantly/OppyAnalytics",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            campaign_id: campaignIdToFetch,
            start_date: start,
            end_date: end,
          }),
        }
      );

      const result = await response.json();

      // Return the fetched result
      return result;
    } catch (error) {
      setError(error.toString());
      console.error(`Error fetching timed data for ${campaignIdToFetch}:`, error);
      return null;
    }
  };

  const fetchAllCampaignsData = async () => {
    const userAllowedCampaignIds = user?.["https://compelling.ai/claims/campaign_ids"] || [];
    
    if (!userAllowedCampaignIds.length) {
      return null;
    }

    const { start, end } = dateRanges[dateRange];

    try {
      console.log("Fetching all campaigns data:", userAllowedCampaignIds);
      console.log("Date range:", start, "to", end);
      
      const response = await fetch(
        "https://api.compelling.ai/instantly/OppyAnalytics",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            endpoint: "all-campaigns",
            campaign_ids: userAllowedCampaignIds,
            start_date: start,
            end_date: end,
          }),
        }
      );

      const result = await response.json();
      console.log("All campaigns API result:", result);
      
      if (!Array.isArray(result) || result.length === 0) {
        console.error("Invalid all campaigns data received");
        return null;
      }

      // Sum up all the metrics from all campaigns
      const totals = result.reduce((acc, campaign) => ({
        leads_count: acc.leads_count + (campaign.leads_count || 0),
        contacted_count: acc.contacted_count + (campaign.contacted_count || 0),
        open_count: acc.open_count + (campaign.open_count || 0),
        reply_count: acc.reply_count + (campaign.reply_count || 0),
        bounced_count: acc.bounced_count + (campaign.bounced_count || 0),
        unsubscribed_count: acc.unsubscribed_count + (campaign.unsubscribed_count || 0),
        completed_count: acc.completed_count + (campaign.completed_count || 0),
        emails_sent_count: acc.emails_sent_count + (campaign.emails_sent_count || 0),
        total_opportunities: acc.total_opportunities + (campaign.total_opportunities || 0),
      }), {
        leads_count: 0,
        contacted_count: 0,
        open_count: 0,
        reply_count: 0,
        bounced_count: 0,
        unsubscribed_count: 0,
        completed_count: 0,
        emails_sent_count: 0,
        total_opportunities: 0,
      });

      // Create summary data compatible with existing display logic
      const combinedSummary = {
        campaign_name: "All Campaigns",
        campaign_id: "all-campaigns",
        total_leads: totals.leads_count,
        leads_who_read: totals.open_count,
        leads_who_replied: totals.reply_count,
        bounced: totals.bounced_count,
        unsubscribed: totals.unsubscribed_count,
        completed: totals.completed_count,
      };

      const timedDataSummary = [{
        new_leads_contacted: totals.contacted_count
      }];

      return {
        summaryData: combinedSummary,
        timedData: timedDataSummary,
        rawCampaigns: result
      };
      
    } catch (error) {
      console.error("Error fetching all campaigns data:", error);
      return null;
    }
  };

  const fetchStepData = async (campaignIdToFetch = selectedCampaignId) => {
    if (!campaignIdToFetch) {
      console.warn("fetchStepData called without a valid campaign ID.");
      return;
    }
    
    const { start, end } = dateRanges[dateRange];
    setStepLoading(true);
    
    try {
      console.log(`Fetching step data for campaign ${campaignIdToFetch} from ${start} to ${end}`);
      
      const requestBody = {
        endpoint: "steps",
        campaign_id: campaignIdToFetch,
        start_date: start,
        end_date: end,
      };
      
      console.log("Step data request body:", requestBody);
      
      const response = await fetch(
        "https://api.compelling.ai/instantly/OppyAnalytics",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      console.log("Step data response status:", response.status);
      const rawResult = await response.text();
      console.log("Step data raw response:", rawResult);
      
      let result;
      try {
        result = JSON.parse(rawResult);
      } catch (e) {
        console.error("Failed to parse step data JSON:", e);
        result = null;
      }
      
      console.log("Step data parsed result:", result);
      
      // Ensure stepData is always an array
      let rawStepData = [];
      
      if (Array.isArray(result)) {
        console.log("Result is already an array, using directly");
        rawStepData = result;
      } else if (result && typeof result === 'object') {
        // If it's an object with data property
        if (Array.isArray(result.data)) {
          console.log("Found array in result.data");
          rawStepData = result.data;
        } else if (result.steps && Array.isArray(result.steps)) {
          console.log("Found array in result.steps");
          rawStepData = result.steps;
        } else {
          // Try to convert object to array if possible
          console.warn("API returned non-array data for steps, attempting to convert:", result);
          try {
            // If it's a collection of step objects keyed by ID
            rawStepData = Object.values(result).filter(item => item && typeof item === 'object');
            console.log("Converted object to array via Object.values():", rawStepData);
          } catch (e) {
            console.error("Failed to process step data:", e);
          }
        }
      }
      
      // Process the raw step data to create the hierarchical structure
      const processedData = processStepData(rawStepData);
      
      console.log("Final processed step data:", processedData);
      setStepData(processedData);
      return processedData;
    } catch (error) {
      setError(error.toString());
      console.error(`Error fetching step data for ${campaignIdToFetch}:`, error);
      setStepData([]);
      return null;
    } finally {
      setStepLoading(false);
    }
  };

  // Process step data to group by step number and calculate totals
  const processStepData = (rawData) => {
    if (!Array.isArray(rawData) || rawData.length === 0) {
      return [];
    }

    // Group steps by step number
    const stepGroups = {};
    
    rawData.forEach(step => {
      // Ensure we have valid step data
      if (!step) return;
      
      // Get the original step number from API (could be 0-indexed)
      let apiStepNumber = parseInt(step.step || "0");
      
      // Convert to 1-indexed for display (add 1 to all steps)
      const displayStepNumber = String(apiStepNumber + 1);
      
      if (!stepGroups[displayStepNumber]) {
        stepGroups[displayStepNumber] = {
          variants: [],
          totals: {
            step: displayStepNumber,
            sent: 0,
            opened: 0,
            unique_opened: 0,
            replies: 0,
            unique_replies: 0,
            clicks: 0,
            unique_clicks: 0,
            opportunities: 0,
            isParent: true
          }
        };
      }
      
      // Add normalized variant info
      const variant = {
        step: displayStepNumber,
        variant: step.variant || "0",
        variantLetter: step.variant === "0" ? "A" : step.variant === "1" ? "B" : String.fromCharCode(65 + parseInt(step.variant || "0")),
        sent: step.sent || 0,
        opened: step.opened || 0,
        unique_opened: step.unique_opened || 0,
        replies: step.replies || 0,
        unique_replies: step.unique_replies || 0,
        clicks: step.clicks || 0,
        unique_clicks: step.unique_clicks || 0,
        opportunities: step.opportunities || 0
      };
      
      stepGroups[displayStepNumber].variants.push(variant);
      
      // Update totals
      const totals = stepGroups[displayStepNumber].totals;
      totals.sent += variant.sent;
      totals.opened += variant.opened;
      totals.unique_opened += variant.unique_opened;
      totals.replies += variant.replies;
      totals.unique_replies += variant.unique_replies;
      totals.clicks += variant.clicks;
      totals.unique_clicks += variant.unique_clicks;
      totals.opportunities += variant.opportunities;
    });
    
    // Convert to flat array with parent rows followed by their variants
    const result = [];
    Object.values(stepGroups).forEach(group => {
      result.push(group.totals); // Add parent row first
      
      // Add variant rows
      group.variants.forEach(variant => {
        result.push(variant);
      });
    });
    
    return result;
  };

  const getCampaignDataById = (campaigns = [], campaignId) => {
    if (!campaigns || !campaignId) return {};
    
    let selectedCampaigns = {};

    for (let i = 0; i < campaigns.length; i++) {
      if (campaigns[i]?.campaign === campaignId) {
        // combine the data from all the campaigns
        selectedCampaigns = { ...selectedCampaigns, ...campaigns[i] };
      }
    }

    return selectedCampaigns;
  };

  // Initial load effect - only runs when user changes
  useEffect(() => {
    console.log("Initial user load useEffect triggered.");
    const userAllowedCampaignIds = user?.["https://compelling.ai/claims/campaign_ids"] || [];
    console.log("User Allowed Campaign IDs:", userAllowedCampaignIds);

    if (!user || userAllowedCampaignIds.length === 0) {
      console.log("No user or no allowed campaigns found.");
      setError("No campaigns assigned or user not fully loaded.");
      setLoading(false);
      setAvailableCampaigns([]);
      setSelectedCampaignId(null);
      setAllData(null);
      setSummaryData(null);
      setTimedData(null);
      setChartData([]);
      setCampaignNames({});
      return;
    }

    setError(null);

    setAvailableCampaigns(userAllowedCampaignIds);
    const initialCampaignId = userAllowedCampaignIds[0];
    setSelectedCampaignId(initialCampaignId);
    console.log("Initially selected campaign ID:", initialCampaignId);

    const currentRangeKey = dateRange;
    const initialStartDate = dateRanges[currentRangeKey].start;
    const initialEndDate = dateRanges[currentRangeKey].end;
    setStartDate(new Date(initialStartDate));
    setEndDate(new Date(initialEndDate));

    setLoading(true);

    console.log(`Fetching core data for initial campaign: ${initialCampaignId}`);
    const coreFetchPromises = [
      fetchAllCampaignAnalytics(),
      fetchSummaryData(initialCampaignId),
      fetchTimedData(initialStartDate, initialEndDate, initialCampaignId)
    ];

    Promise.all(coreFetchPromises)
      .then(([allFetchedDataResult, summaryResult, timedResult]) => {
          console.log("Core data fetched.", { allFetchedDataResult, summaryResult, timedResult });

          let filteredData = [];
          if (allFetchedDataResult && Array.isArray(allFetchedDataResult)) {
             filteredData = allFetchedDataResult.filter(campaign =>
               userAllowedCampaignIds.includes(campaign?.campaign)
             );
             console.log("Filtered campaign data (for analytics):", filteredData);
             setAllData(filteredData);
          } else {
             console.error("Invalid or missing bulk analytics data received.");
             setAllData([]);
          }

          if (summaryResult) {
             console.log("Setting initial summary data:", summaryResult);
             setSummaryData(summaryResult);
          } else {
             console.error("Failed to fetch initial summary data.");
             setError(prev => prev ? `${prev}\nFailed to load initial summary.` : 'Failed to load initial summary.');
             setSummaryData(null);
          }

          if (timedResult) {
             console.log("Setting initial timed data:", timedResult);
             setTimedData(timedResult);
          } else {
             console.error("Failed to fetch initial timed data.");
             setError(prev => prev ? `${prev}\nFailed to load initial timed data.` : 'Failed to load initial timed data.');
             setTimedData(null);
          }

          if (filteredData.length > 0 && initialCampaignId) {
            console.log("Updating chart with initial data...");
            updateChartData(filteredData, initialCampaignId, currentRangeKey, chartAggregation);
          } else {
            console.log("No filtered analytics data or initial campaign ID, setting empty chart data.");
            setChartData([]);
          }

          console.log("Initial data processing complete. Setting loading to false.");
          setLoading(false);

          const otherCampaignIds = userAllowedCampaignIds.filter(id => id !== initialCampaignId);
          if (otherCampaignIds.length > 0) {
            console.log("Fetching remaining campaign names sequentially in background:", otherCampaignIds);
            (async () => {
              for (const id of otherCampaignIds) {
                try {
                  console.log(`Background fetching name for: ${id}`);
                  await fetchSummaryData(id);
                } catch (err) {
                  console.warn(`Failed to fetch name for campaign ${id} in background:`, err);
                }
              }
              console.log("Background name fetching complete.");
            })();
          }

        })
      .catch(error => {
          console.error('Fatal error during initial core data fetch Promise.all:', error);
          setAllData([]);
          setCampaignNames({});
          setChartData([]);
          setSummaryData(null);
          setTimedData(null);
          setError("Failed to load initial campaign data. Please check connection or try again.");
          setLoading(false);
        });

  }, [user]); // Only depend on user, not dateRange

  useEffect(() => {
    if (loading || !selectedCampaignId || !user) return;

    // Handle "All Campaigns" selection
    if (selectedCampaignId === "all-campaigns") {
      console.log(`Processing All Campaigns for date range: ${dateRange}`);
      setLoading(true);

      // Reset to activity tab when selecting all campaigns
      if (activeTab === "step-analytics") {
        setActiveTab("activity");
      }

      // Clear step data since it's not available for all campaigns
      setStepData([]);

      // Fetch comprehensive data for all campaigns
      fetchAllCampaignsData()
        .then((allCampaignsResult) => {
          if (allCampaignsResult) {
            setSummaryData(allCampaignsResult.summaryData);
            setTimedData(allCampaignsResult.timedData);
            setError(null);

            // Generate chart data from allData (aggregated across all campaigns)
            if (allData && allData.length > 0) {
              updateChartData(allData, "all-campaigns", dateRange, chartAggregation);
            } else {
              setChartData([]);
            }
          } else {
            setError("Failed to fetch data for All Campaigns.");
            setChartData([]);
            setSummaryData(null);
            setTimedData(null);
          }
        })
        .catch(error => {
          console.error("Error fetching All Campaigns data:", error);
          setError("Failed to load All Campaigns data.");
          setChartData([]);
          setSummaryData(null);
          setTimedData(null);
        })
        .finally(() => {
          setLoading(false);
        });

      return;
    }

    console.log(`Fetching details for changed selection: ${selectedCampaignId}, Range: ${dateRange}`);
    const { start, end } = dateRanges[dateRange];

    setLoading(true);

    // Determine if we only changed the date range or also changed the campaign
    const fetchPromises = [
      // Only fetch time-sensitive data
      fetchTimedData(start, end, selectedCampaignId),
      fetchStepData(selectedCampaignId)
    ];
    
    // Only fetch summary data if we don't already have it (i.e., campaign changed)
    if (!summaryData || summaryData.campaign_id !== selectedCampaignId) {
      fetchPromises.unshift(fetchSummaryData(selectedCampaignId));
    } else {
      // Add a placeholder result for consistency in the Promise.all result array
      fetchPromises.unshift(Promise.resolve(summaryData));
    }

    Promise.all(fetchPromises)
      .then(([summaryResult, timedResult, stepsResult]) => {
          console.log("Updated data fetched on change:", { summaryResult, timedResult, stepsResult });

          if (summaryResult) {
             setSummaryData(summaryResult);
          } else {
             console.error(`Failed to fetch summary for ${selectedCampaignId} on change.`);
             setError(prev => prev ? `${prev}\nFailed update summary.` : 'Failed update summary.');
          }

          if (timedResult) {
             setTimedData(timedResult);
          } else {
              setError(prev => prev ? `${prev}\nFailed update timed data.` : 'Failed update timed data.');
          }

          if(allData) {
            console.log("Updating chart data on change...");
            updateChartData(allData, selectedCampaignId, dateRange, chartAggregation);
          } else {
             console.warn("Cannot update chart on change, bulk analytics data (allData) is missing.");
             setChartData([]);
          }
      })
      .catch(error => {
        console.error('Error fetching data on selection/range change:', error);
        setError("Failed to update campaign details. Please try again.");
      })
      .finally(() => {
        console.log("Finished processing selection/range change.");
        setLoading(false);
      });

  }, [dateRange, selectedCampaignId]);

  // Also fetch step data when tab changes to step analytics
  useEffect(() => {
    if (activeTab === "step-analytics" && selectedCampaignId && selectedCampaignId !== "all-campaigns" && !stepLoading && stepData.length === 0) {
      fetchStepData(selectedCampaignId);
    }
  }, [activeTab]);

  // Update chart when aggregation changes
  useEffect(() => {
    if (!loading && allData && selectedCampaignId) {
      updateChartData(allData, selectedCampaignId, dateRange, chartAggregation);
    }
  }, [chartAggregation]);

  function updateChartData(sourceData, currentCampaignId, currentRangeKey, aggregation = "daily") {
    console.log(`updateChartData called with campaignId: ${currentCampaignId}, rangeKey: ${currentRangeKey}, aggregation: ${aggregation}`);
    if (!sourceData || !currentCampaignId) {
      setChartData([]);
      return;
    }

    function generateDateRange(startDate, endDate) {
      const dates = [];
      let currentDate = dayjs(startDate);
      const finalDate = dayjs(endDate);
      while (currentDate.isBefore(finalDate) || currentDate.isSame(finalDate)) {
        dates.push(currentDate.format("YYYY-MM-DD"));
        currentDate = currentDate.add(1, 'day');
      }
      return dates;
    }

    function extractDayMetrics(campaignData, date) {
      const dayData = campaignData[date] || {};
      return {
        sent: Object.values(dayData.sent || {}).reduce(
          (acc, curr) => acc + Object.values(curr).reduce((a, b) => a + b, 0),
          0
        ),
        replies: Object.values(dayData.reply_volume || {}).reduce(
          (acc, curr) => acc + Object.values(curr).reduce((a, b) => a + b, 0),
          0
        ),
      };
    }

    const { start, end } = dateRanges[currentRangeKey];
    const allDates = generateDateRange(start, end);

    let newChartData;

    if (currentCampaignId === "all-campaigns") {
      // Aggregate data across all campaigns
      newChartData = allDates.map((date) => {
        let totalSent = 0;
        let totalReplies = 0;

        sourceData.forEach((campaign) => {
          const metrics = extractDayMetrics(campaign, date);
          totalSent += metrics.sent;
          totalReplies += metrics.replies;
        });

        return { date, sent: totalSent, replies: totalReplies };
      });
    } else {
      // Single campaign data
      const campaignData = getCampaignDataById(sourceData, currentCampaignId);
      if (!campaignData) {
        setChartData([]);
        return;
      }

      newChartData = allDates.map((date) => {
        const metrics = extractDayMetrics(campaignData, date);
        return { date, ...metrics };
      });
    }

    // Aggregate by week if requested
    if (aggregation === "weekly") {
      const weeklyData = {};
      newChartData.forEach((dayEntry) => {
        const weekStart = dayjs(dayEntry.date).startOf("week").format("YYYY-MM-DD");
        if (!weeklyData[weekStart]) {
          weeklyData[weekStart] = { date: weekStart, sent: 0, replies: 0 };
        }
        weeklyData[weekStart].sent += dayEntry.sent;
        weeklyData[weekStart].replies += dayEntry.replies;
      });
      newChartData = Object.values(weeklyData).sort((a, b) =>
        dayjs(a.date).valueOf() - dayjs(b.date).valueOf()
      );
    }

    setChartData(newChartData);
  }

  // console.log("Rendering check:", { loading, selectedCampaignId, error, summaryData, allData, timedData });

  if (loading) {
    // return a the skeleton of the chart based on the current layout
    return (
      <>
      <Nav compellingUser={compellingUser} />
      <div className="p-20 pt-40">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-5 items-center">
          <Skeleton className="h-10 w-full" />
          <div></div>
          <Skeleton className="h-10 w-full" />
        </div>
        <Tabs defaultValue="activity" className="mb-5">
          <TabsList>
            <TabsTrigger value="activity">
              <Skeleton className="h-6 w-20" />
            </TabsTrigger>
            <TabsTrigger value="step-analytics">
              <Skeleton className="h-6 w-20" />
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-5">
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-24" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-16" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-24" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-16" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-24" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-16" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-24" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-32" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-24" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-16" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-24" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-16" />
            </CardContent>
          </Card>
        </div>
        <Card className="mt-5 pt-10 pr-6">
          <CardContent>
            <Skeleton className="min-h-[250px] max-h-[300px] w-full" />
          </CardContent>
        </Card>
      </div>
      </>
    );
  }

  if (!selectedCampaignId) {
    return <div className="text-xl">No campaign selected.</div>;
  }

  if (error) {
    return <div className="text-xl">Error: {error}</div>;
  }

  // Both "All Campaigns" and individual campaigns now use summaryData
  // For individual campaigns, we also need allData for chart generation
  if (!summaryData || !timedData || (selectedCampaignId !== "all-campaigns" && !allData)) {
    return null;
  }

  const {
    campaign_name,
    total_leads,
    leads_who_read,
    leads_who_replied,
    bounced,
    unsubscribed,
    completed,
  } = summaryData;

  const contacted = timedData[0]?.new_leads_contacted || 0;

  // For "All Campaigns", use the reply count from summaryData, otherwise calculate from chart
  const totalReplies = selectedCampaignId === "all-campaigns" 
    ? (leads_who_replied || 0)
    : (chartData?.reduce((acc, curr) => acc + curr.replies, 0) || 0);
  
  const totalSent = chartData?.reduce((acc, curr) => acc + curr.sent, 0) || 0;
  
  // Fix for reply rate calculation - use either contacted or totalSent as denominator, 
  // and ensure we don't show 0% when there are replies
  const effectiveDenominator = contacted || totalSent || 1; // Fallback to 1 to avoid division by zero
  const replyRate = totalReplies > 0 
    ? ((totalReplies / effectiveDenominator) * 100).toFixed(1) 
    : "0";

  const chartConfig = {
    sent: {
      label: "Sent",
      color: "hsl(var(--chart-1))",
    },
    replies: {
      label: "Replies",
      color: "hsl(var(--chart-2))",
    },
  };

  if (!chartData) {
    setChartData([]);
  }

  return (
    <>
      <Nav compellingUser={compellingUser} />
      <div className="p-20 pt-40">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-5 items-center">
          <Select
            value={selectedCampaignId}
            onValueChange={(value) => setSelectedCampaignId(value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select campaign">
                {summaryData?.campaign_name || "Select campaign"}
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all-campaigns">All Campaigns</SelectItem>
              {availableCampaigns.map((campaignId) => (
                <SelectItem key={campaignId} value={campaignId}>
                  {campaignNames[campaignId] || campaignId}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div></div>
          <Select
            value={dateRange}
            onValueChange={(value) => setDateRange(value)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="month_to_date">Month to date</SelectItem>
              <SelectItem value="last_7_days">Last 7 days</SelectItem>
              <SelectItem value="last_30_days">Last 4 weeks</SelectItem>
              <SelectItem value="last_90_days">Last 3 months</SelectItem>
              <SelectItem value="last_180_days">Last 6 months</SelectItem>
              <SelectItem value="last_365_days">Last 12 months</SelectItem>
              <SelectItem disabled className="text-xs text-gray-400 font-semibold">── {dayjs().year()} ──</SelectItem>
              <SelectItem value={`q1_${dayjs().year()}`}>Q1 {dayjs().year()}</SelectItem>
              <SelectItem value={`q2_${dayjs().year()}`}>Q2 {dayjs().year()}</SelectItem>
              <SelectItem value={`q3_${dayjs().year()}`}>Q3 {dayjs().year()}</SelectItem>
              <SelectItem value={`q4_${dayjs().year()}`}>Q4 {dayjs().year()}</SelectItem>
              <SelectItem disabled className="text-xs text-gray-400 font-semibold">── {dayjs().year() - 1} ──</SelectItem>
              <SelectItem value={`q1_${dayjs().year() - 1}`}>Q1 {dayjs().year() - 1}</SelectItem>
              <SelectItem value={`q2_${dayjs().year() - 1}`}>Q2 {dayjs().year() - 1}</SelectItem>
              <SelectItem value={`q3_${dayjs().year() - 1}`}>Q3 {dayjs().year() - 1}</SelectItem>
              <SelectItem value={`q4_${dayjs().year() - 1}`}>Q4 {dayjs().year() - 1}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <Tabs value={activeTab} onValueChange={(value) => {
          // Prevent switching to step-analytics when all campaigns is selected
          if (value === "step-analytics" && selectedCampaignId === "all-campaigns") {
            return;
          }
          setActiveTab(value);
        }} className="mb-5">
          <TabsList>
            <TabsTrigger value="activity">Activity</TabsTrigger>
            <TabsTrigger 
              value="step-analytics" 
              disabled={selectedCampaignId === "all-campaigns"}
              style={selectedCampaignId === "all-campaigns" ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
            >
              Step Analytics
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="activity">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-5">
              <Card>
                <CardHeader>
                  <div className="text-lg font-medium">Total Leads</div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{total_leads}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <div className="text-lg font-medium">Contacted</div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{contacted}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <div className="text-lg font-medium">Opened</div>
                </CardHeader>
                <CardContent>
                  <div
                    className={
                      leads_who_read === 0 && selectedCampaignId !== "all-campaigns"
                        ? "text-2xl font-bold text-gray-300"
                        : "text-2xl font-bold"
                    }
                  >
                    {leads_who_read === 0 && selectedCampaignId !== "all-campaigns" ? "Not tracked" : leads_who_read}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <div className="text-lg font-medium">Reply Rate</div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold flex items-center">
                    {replyRate}%
                    <span className="text-sm ml-2 font-normal">
                      | {totalReplies} Replies
                    </span>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <div className="text-lg font-medium">Bounced</div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{bounced ?? 0}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <div className="text-lg font-medium">Completed</div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{completed}</div>
                </CardContent>
              </Card>
            </div>
            <Card className="mt-5 pt-6 pr-6">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <div className="text-lg font-medium">
                  {chartAggregation === "weekly" ? "Weekly" : "Daily"} Activity
                </div>
                <div className="flex gap-1 bg-gray-100 rounded-md p-1">
                  <button
                    onClick={() => setChartAggregation("daily")}
                    className={`px-3 py-1 text-sm rounded ${
                      chartAggregation === "daily"
                        ? "bg-white shadow-sm font-medium"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    Daily
                  </button>
                  <button
                    onClick={() => setChartAggregation("weekly")}
                    className={`px-3 py-1 text-sm rounded ${
                      chartAggregation === "weekly"
                        ? "bg-white shadow-sm font-medium"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    Weekly
                  </button>
                </div>
              </CardHeader>
              <CardContent>
                <ChartContainer
                  config={chartConfig}
                  className="min-h-[250px] max-h-[300px]  w-full"
                >
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="fillSent" x1="0" y1="0" x2="0" y2="1">
                        <stop
                          offset="5%"
                          stopColor="hsl(var(--chart-1))"
                          stopOpacity={0.8}
                        />
                        <stop
                          offset="95%"
                          stopColor="hsl(var(--chart-1))"
                          stopOpacity={0.1}
                        />
                      </linearGradient>
                      <linearGradient id="fillReplies" x1="0" y1="0" x2="0" y2="1">
                        <stop
                          offset="5%"
                          stopColor="hsl(var(--chart-2))"
                          stopOpacity={0.8}
                        />
                        <stop
                          offset="95%"
                          stopColor="hsl(var(--chart-2))"
                          stopOpacity={0.1}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid vertical={false} strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tickLine={false}
                      axisLine={false}
                      tickMargin={8}
                      minTickGap={32}
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        if (chartAggregation === "weekly") {
                          return `Week of ${date.toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                          })}`;
                        }
                        return date.toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                        });
                      }}
                    />
                    <YAxis />
                    <Tooltip
                      cursor={{ stroke: "#ccc", strokeDasharray: "5 5" }}
                      content={
                        <ChartTooltipContent
                          labelFormatter={(value) => {
                            const date = new Date(value);
                            if (chartAggregation === "weekly") {
                              return `Week of ${date.toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                              })}`;
                            }
                            return date.toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                            });
                          }}
                          indicator="dot"
                        />
                      }
                    />
                    <Area
                      dataKey="sent"
                      type="linear"
                      fill="url(#fillSent)"
                      stroke="hsl(var(--chart-1))"
                      activeDot={{ r: 5 }}
                      dot={{ r: 3 }}
                    />
                    <Area
                      dataKey="replies"
                      type="linear"
                      fill="url(#fillReplies)"
                      stroke="hsl(var(--chart-2))"
                      activeDot={{ r: 5 }}
                      dot={{ r: 3 }}
                    />
                    <ChartLegend content={<ChartLegendContent />} />
                  </AreaChart>
                </ChartContainer>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="step-analytics">
            {(() => {
              console.log("Rendering step analytics tab. Step data:", stepData);
              console.log("Step data type:", typeof stepData);
              console.log("Is array:", Array.isArray(stepData));
              console.log("Length:", stepData?.length);
              
              if (stepLoading) {
                return (
                  <div className="flex justify-center items-center min-h-[200px]">
                    <Skeleton className="h-[400px] w-full" />
                  </div>
                );
              } 
              
              if (!stepData || !Array.isArray(stepData) || stepData.length === 0) {
                return (
                  <div className="flex justify-center items-center min-h-[200px]">
                    <p className="text-lg text-gray-500">No step data available. Try adjusting your date range.</p>
                    <button 
                      className="ml-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                      onClick={() => fetchStepData()}
                    >
                      Retry Fetch
                    </button>
                  </div>
                );
              }
              
              return (
                <Card>
                  <CardContent className="pt-6">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>STEP</TableHead>
                          <TableHead>SENT</TableHead>
                          <TableHead>OPENED</TableHead>
                          <TableHead>REPLIED</TableHead>
                          <TableHead>CLICKED</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {stepData.map((step, index) => {
                          console.log(`Rendering step ${index}:`, step);
                          
                          // Calculate reply rate percentage with safety checks
                          const replies = step.unique_replies || step.replies || 0;
                          const sent = step.sent || 1; // Avoid division by zero
                          const replyRate = sent > 0 ? ((replies / sent) * 100).toFixed(1) : 0;
                          
                          const isParent = step.isParent;
                          const isVariant = step.variantLetter && !isParent;
                          
                          return (
                            <TableRow 
                              key={`step-${index}`}
                              className={isParent ? "border-t-2 font-medium" : "pl-4"}
                            >
                              <TableCell className={isVariant ? "pl-10" : ""}>
                                {isParent ? (
                                  `Step ${step.step}`
                                ) : (
                                  step.variantLetter
                                )}
                              </TableCell>
                              <TableCell>{step.sent || 0}</TableCell>
                              <TableCell className="text-gray-300">Not tracked</TableCell>
                              <TableCell>
                                {replies} {isParent || replies > 0 ? `| ${replyRate}%` : ""}
                              </TableCell>
                              <TableCell className="text-gray-300">Not tracked</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              );
            })()}
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
};

export default Analytics;
